/**
 * Configurable Products - Dynamic Implementation v3.0
 * 
 * Now includes quantity selector and bulk discount pricing
 * Supports VELUX and Dakea tiered discount tags
 * 
 * @author Claude
 * @version 3.0.1
 */

class ConfigurableProduct {
  constructor(container) {
    this.container = container;
    this.childProducts = [];
    this.productCache = {};
    this.attributes = [];
    this.selectors = {};
    this.selections = {};
    this.selectedProduct = null;
    this.isLoading = true;
    
    // Configuration
    this.config = {
      metafieldName: 'configurable',
      attributeMetafieldPrefix: 'custom',
      cacheTimeout: 300000,
      enableDebugMode: false
    };
    
    this.init();
  }
  
  async init() {
    try {
      this.log('Initializing configurable product system');
      
      this.showLoading();
      
      const preloadedData = this.container.dataset.childProductsData;
      
      if (preloadedData) {
        this.log('Using pre-loaded product data from Liquid');
        try {
          this.childProducts = JSON.parse(preloadedData);
          this.log(`Loaded ${this.childProducts.length} child products from Liquid data`);
        } catch (e) {
          this.log('Failed to parse pre-loaded data', 'error', e);
          throw new Error('Invalid product data');
        }
      } else {
        this.log('No pre-loaded data, fetching from API');
        const childHandles = this.getChildProductHandles();
        
        if (!childHandles || childHandles.length === 0) {
          this.log('No child products found in metafield', 'warn');
          this.showError('No product variations configured');
          return;
        }
        
        await this.fetchChildProducts(childHandles);
      }
      
      if (this.childProducts.length === 0) {
        this.showError('Unable to load product variations');
        return;
      }
      
      this.detectAttributes();
      
      if (this.attributes.length === 0) {
        this.log('No varying attributes detected', 'warn');
        this.showError('Product variations not properly configured');
        return;
      }
      
      this.log(`Detected ${this.attributes.length} varying attributes:`, this.attributes);
      
      this.buildUI();
      this.attachEventListeners();
      this.updatePriceDisplay();
      this.hideLoading();
      
      this.isLoading = false;
      this.log('Initialization complete');
      
    } catch (error) {
      this.log('Initialization failed', 'error', error);
      this.showError('Failed to initialize product options');
    }
  }
  
  getChildProductHandles() {
    const metafield = this.container.dataset.childProducts;
    
    if (!metafield || metafield.trim() === '') {
      return [];
    }
    
    if (metafield.includes(';')) {
      return metafield
        .split(';')
        .map(handle => handle.trim())
        .filter(handle => handle.length > 0);
    }
    
    if (metafield.includes(',')) {
      return metafield
        .split(',')
        .map(handle => handle.trim())
        .filter(handle => handle.length > 0);
    }
    
    try {
      const parsed = JSON.parse(metafield);
      if (Array.isArray(parsed)) {
        return parsed.map(item => {
          if (typeof item === 'string') return item;
          if (item.handle) return item.handle;
          return null;
        }).filter(Boolean);
      }
    } catch (e) {
      // Not JSON
    }
    
    return [metafield.trim()];
  }
  
  async fetchChildProducts(handles) {
    this.log(`Fetching ${handles.length} child products...`);
    
    const fetchPromises = handles.map(handle => this.fetchProduct(handle));
    const results = await Promise.allSettled(fetchPromises);
    
    results.forEach((result, index) => {
      if (result.status === 'fulfilled' && result.value) {
        this.childProducts.push(result.value);
        this.productCache[result.value.handle] = result.value;
      } else {
        this.log(`Failed to fetch product: ${handles[index]}`, 'warn');
      }
    });
    
    this.log(`Successfully loaded ${this.childProducts.length} products`);
  }
  
  async fetchProduct(handle) {
    try {
      const response = await fetch(`/products/${handle}.js`);
      
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }
      
      const product = await response.json();
      
      return {
        handle: product.handle,
        title: product.title,
        price: product.price,
        compareAtPrice: product.compare_at_price,
        available: product.available,
        variantId: product.variants[0]?.id,
        sku: product.variants[0]?.sku,
        images: product.images,
        tags: product.tags,
        metafields: this.extractMetafields(product)
      };
      
    } catch (error) {
      this.log(`Error fetching product ${handle}:`, 'error', error);
      return null;
    }
  }
  
  extractMetafields(product) {
    return {};
  }
  
  detectAttributes() {
    if (this.childProducts.length === 0) return;
    
    const allMetafieldKeys = new Set();
    this.childProducts.forEach(product => {
      if (product.metafields) {
        Object.keys(product.metafields).forEach(key => allMetafieldKeys.add(key));
      }
    });
    
    this.log('All metafield keys found:', Array.from(allMetafieldKeys));
    
    const varyingAttributes = [];
    
    allMetafieldKeys.forEach(key => {
      const values = new Set();
      let hasValue = false;
      
      this.childProducts.forEach(product => {
        if (product.metafields && product.metafields[key]) {
          values.add(product.metafields[key]);
          hasValue = true;
        }
      });
      
      if (hasValue && values.size > 0) {
        varyingAttributes.push({
          key: key,
          label: this.formatAttributeLabel(key),
          values: Array.from(values).sort(this.sortAttributeValues)
        });
      }
    });
    
    this.attributes = this.sortAttributes(varyingAttributes);
  }
  
  formatAttributeLabel(key) {
    const labelMap = {
      'height': 'Height',
      'width': 'Width',
      'frame_finish': 'Frame Finish',
      'glazing': 'Glazing',
      'colour': 'Colour',
      'color': 'Color',
      'blind_operation': 'Blind Operation',
      'flashings_conservation': 'Flashings',
      'combination': 'Combination',
      'flashing_cabrio': 'Flashing Type',
      'cover': 'Cover',
      'tunnel_diameter': 'Tunnel Diameter',
      'tunnel_type': 'Tunnel Type'
    };
    
    if (labelMap[key]) {
      return labelMap[key];
    }
    
    return key
      .split('_')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  }
  
  sortAttributeValues(a, b) {
    const aNum = parseFloat(a);
    const bNum = parseFloat(b);
    
    if (!isNaN(aNum) && !isNaN(bNum)) {
      return aNum - bNum;
    }
    
    return a.localeCompare(b, undefined, { numeric: true, sensitivity: 'base' });
  }
  
  sortAttributes(attributes) {
    const orderPriority = {
      'frame_finish': 1,
      'colour': 2,
      'color': 2,
      'height': 3,
      'width': 4,
      'glazing': 5,
      'combination': 6
    };
    
    return attributes.sort((a, b) => {
      const aPriority = orderPriority[a.key] || 999;
      const bPriority = orderPriority[b.key] || 999;
      
      if (aPriority !== bPriority) {
        return aPriority - bPriority;
      }
      
      return a.label.localeCompare(b.label);
    });
  }
  
  buildUI() {
    this.log('Building UI...');
    
    const existingContent = this.container.querySelectorAll(':not(.config-loading)');
    existingContent.forEach(el => el.remove());
    
    const priceStartHTML = `
      <div class="config-price-start" id="config-price-start">
        <span class="label">Prices start from</span>
        <span class="price-amount"></span>
      </div>
    `;
    this.container.insertAdjacentHTML('beforeend', priceStartHTML);
    this.priceStartEl = this.container.querySelector('#config-price-start .price-amount');
    
    this.attributes.forEach((attribute, index) => {
      const isFirst = index === 0;
      
      const optionHTML = `
        <div class="config-option" data-attribute="${attribute.key}">
          <label for="config-select-${attribute.key}">
            ${attribute.label}
            <span class="required">*</span>
          </label>
          <select 
            id="config-select-${attribute.key}"
            class="config-select"
            data-attribute="${attribute.key}"
            data-index="${index}"
            ${!isFirst ? 'disabled' : ''}
            required
          >
            <option value="">Select ${attribute.label}</option>
          </select>
        </div>
      `;
      
      this.container.insertAdjacentHTML('beforeend', optionHTML);
      const select = this.container.querySelector(`#config-select-${attribute.key}`);
      this.selectors[attribute.key] = select;
    });
    
    if (this.attributes.length > 0) {
      this.populateDropdown(this.attributes[0].key);
    }
    
    const infoHTML = `
      <div class="config-info" id="config-info" style="display: none;">
        <div class="config-price" id="config-price"></div>
        <div class="config-availability" id="config-availability"></div>
        <div class="config-sku" id="config-sku"></div>
        <div class="config-bulk-discount" id="config-bulk-discount"></div>
      </div>
    `;
    this.container.insertAdjacentHTML('beforeend', infoHTML);
    
    this.configInfoEl = this.container.querySelector('#config-info');
    this.priceEl = this.container.querySelector('#config-price');
    this.availabilityEl = this.container.querySelector('#config-availability');
    this.skuEl = this.container.querySelector('#config-sku');
    this.bulkDiscountEl = this.container.querySelector('#config-bulk-discount');
    
    // Create quantity selector and Add to Cart button together
    const quantityAndButtonHTML = `
      <div class="config-quantity-wrapper" style="display: flex; align-items: center; gap: 12px; margin-top: 16px;">
        <div class="quantity-selector" id="config-quantity-selector" style="display: flex; align-items: center; border: 1px solid #ddd; border-radius: 4px; opacity: 0.5; pointer-events: none;">
          <button 
            type="button" 
            class="quantity-minus button-unstyled" 
            id="config-quantity-minus"
            disabled
            style="padding: 0; width: 44px; height: 52px; border: none; background: none; cursor: pointer; display: flex; align-items: center; justify-content: center; font-size: 20px; line-height: 1;"
          >
            <span class="visually-hidden">Decrease quantity</span>
            <span style="pointer-events: none;">−</span>
          </button>
          <input 
            type="number" 
            id="config-quantity-input"
            value="1" 
            min="1" 
            step="1"
            disabled
            aria-label="Quantity"
            style="width: 60px; height: 52px; text-align: center; border: none; padding: 0 8px; font-size: 16px; -moz-appearance: textfield; background: transparent;"
          >
          <button 
            type="button" 
            class="quantity-plus button-unstyled" 
            id="config-quantity-plus"
            disabled
            style="padding: 0; width: 44px; height: 52px; border: none; background: none; cursor: pointer; display: flex; align-items: center; justify-content: center; font-size: 20px; line-height: 1;"
          >
            <span class="visually-hidden">Increase quantity</span>
            <span style="pointer-events: none;">+</span>
          </button>
        </div>
        <button 
          type="button" 
          class="config-add-to-cart button button--primary" 
          id="config-add-cart" 
          disabled
          style="flex: 1; height: 52px; display: flex; align-items: center; justify-content: center; gap: 8px; position: relative;"
        >
          <span class="add-to-cart-text" style="display: flex; gap: 8px; align-items: center; justify-content: center;">
            <span class="svg-wrapper add-to-cart-icon"></span>
            <span class="add-to-cart-text__content">Add to Cart</span>
          </span>
          <span class="add-to-cart-text--added" style="position: absolute; inset: 0; display: flex; align-items: center; justify-content: center; gap: 8px; opacity: 0;">
            <span class="svg-wrapper add-to-cart-icon--added"></span>
            <span>Added</span>
          </span>
        </button>
      </div>
      
      <style>
        /* Hide number input spinners */
        #config-quantity-input::-webkit-outer-spin-button,
        #config-quantity-input::-webkit-inner-spin-button {
          -webkit-appearance: none;
          margin: 0;
        }
        
        /* Active state for quantity selector */
        .quantity-selector.active {
          opacity: 1 !important;
          pointer-events: auto !important;
        }
        
        .quantity-selector.active button {
          cursor: pointer;
        }
        
        /* Button animations */
        .config-add-to-cart.atc-added .add-to-cart-text {
          animation: atc-slide-out 0.3s ease forwards;
        }
        
        .config-add-to-cart.atc-added .add-to-cart-text--added {
          animation: atc-slide-in 0.3s ease forwards;
        }
        
        @keyframes atc-slide-in {
          from {
            opacity: 0;
            transform: translateY(0.5em);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        
        @keyframes atc-slide-out {
          from {
            transform: translateY(0);
            opacity: 1;
          }
          to {
            transform: translateY(-1em);
            opacity: 0;
          }
        }
      </style>
    `;
    
    this.container.insertAdjacentHTML('beforeend', quantityAndButtonHTML);
    
    this.addToCartBtn = this.container.querySelector('#config-add-cart');
    this.quantityInput = this.container.querySelector('#config-quantity-input');
    this.quantityMinusBtn = this.container.querySelector('#config-quantity-minus');
    this.quantityPlusBtn = this.container.querySelector('#config-quantity-plus');
    this.quantitySelector = this.container.querySelector('#config-quantity-selector');
    
    this.setupQuantitySelector();
    
    // Inject SVG icons from theme assets
    this.injectButtonIcons();
    
    const minPrice = Math.min(...this.childProducts.map(p => p.price));
    if (this.priceStartEl) {
      this.priceStartEl.textContent = this.formatMoney(minPrice);
    }
  }
  
  setupQuantitySelector() {
    if (!this.quantityInput || !this.quantityMinusBtn || !this.quantityPlusBtn) return;
    
    this.quantityMinusBtn.addEventListener('click', () => {
      const currentValue = parseInt(this.quantityInput.value) || 1;
      const minValue = parseInt(this.quantityInput.min) || 1;
      if (currentValue > minValue) {
        this.quantityInput.value = currentValue - 1;
      }
    });
    
    this.quantityPlusBtn.addEventListener('click', () => {
      const currentValue = parseInt(this.quantityInput.value) || 1;
      const maxValue = parseInt(this.quantityInput.max);
      if (!maxValue || currentValue < maxValue) {
        this.quantityInput.value = currentValue + 1;
      }
    });
    
    this.quantityInput.addEventListener('blur', () => {
      const value = parseInt(this.quantityInput.value);
      const minValue = parseInt(this.quantityInput.min) || 1;
      const maxValue = parseInt(this.quantityInput.max);
      
      if (isNaN(value) || value < minValue) {
        this.quantityInput.value = minValue;
      } else if (maxValue && value > maxValue) {
        this.quantityInput.value = maxValue;
      }
    });
    
    this.quantityInput.addEventListener('keypress', (e) => {
      if (e.key && !/[0-9]/.test(e.key)) {
        e.preventDefault();
      }
    });
  }
  
  enableQuantitySelector() {
    if (this.quantitySelector) {
      this.quantitySelector.classList.add('active');
      this.quantityInput.disabled = false;
      this.quantityMinusBtn.disabled = false;
      this.quantityPlusBtn.disabled = false;
    }
  }
  
  disableQuantitySelector() {
    if (this.quantitySelector) {
      this.quantitySelector.classList.remove('active');
      this.quantityInput.disabled = true;
      this.quantityMinusBtn.disabled = true;
      this.quantityPlusBtn.disabled = true;
      this.quantityInput.value = 1; // Reset to 1
    }
  }
  
  injectButtonIcons() {
    // Fetch and inject the cart icon from existing page elements
    const cartIconWrapper = this.addToCartBtn.querySelector('.add-to-cart-icon');
    const checkmarkIconWrapper = this.addToCartBtn.querySelector('.add-to-cart-icon--added');
    
    if (cartIconWrapper) {
      // Try to get icon from existing native add-to-cart button
      const existingCartIcon = document.querySelector('add-to-cart-component .add-to-cart-icon svg');
      if (existingCartIcon) {
        cartIconWrapper.innerHTML = existingCartIcon.outerHTML;
      }
    }
    
    if (checkmarkIconWrapper) {
      // Try to get checkmark from existing native button
      const existingCheckmark = document.querySelector('add-to-cart-component .add-to-cart-icon--added svg');
      if (existingCheckmark) {
        checkmarkIconWrapper.innerHTML = existingCheckmark.outerHTML;
      } else {
        // Fallback checkmark SVG
        checkmarkIconWrapper.innerHTML = `
          <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M16.6667 5L7.50004 14.1667L3.33337 10" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
          </svg>
        `;
      }
    }
  }
  
  populateDropdown(attributeKey, availableProducts = null) {
    const select = this.selectors[attributeKey];
    if (!select) return;
    
    const attribute = this.attributes.find(attr => attr.key === attributeKey);
    if (!attribute) return;
    
    const products = availableProducts || this.childProducts;
    
    const availableValues = new Set();
    products.forEach(product => {
      if (product.metafields && product.metafields[attributeKey]) {
        availableValues.add(product.metafields[attributeKey]);
      }
    });
    
    const sortedValues = Array.from(availableValues).sort(this.sortAttributeValues);
    
    const currentValue = select.value;
    
    select.innerHTML = `<option value="">Select ${attribute.label}</option>`;
    
    sortedValues.forEach(value => {
      const option = document.createElement('option');
      option.value = value;
      option.textContent = value;
      select.appendChild(option);
    });
    
    if (currentValue && sortedValues.includes(currentValue)) {
      select.value = currentValue;
    }
    
    this.log(`Populated ${attributeKey} with ${sortedValues.length} options`);
  }
  
  attachEventListeners() {
    Object.entries(this.selectors).forEach(([key, select]) => {
      select.addEventListener('change', () => this.handleSelectionChange(key));
    });
    
    this.addToCartBtn.addEventListener('click', () => this.addToCart());
  }
  
  handleSelectionChange(changedKey) {
    const select = this.selectors[changedKey];
    const value = select.value;
    const index = parseInt(select.dataset.index);
    
    this.log(`Selection changed: ${changedKey} = ${value}`);
    
    this.selections[changedKey] = value;
    
    this.attributes.forEach((attr, idx) => {
      if (idx > index) {
        const sel = this.selectors[attr.key];
        sel.value = '';
        sel.disabled = true;
        this.selections[attr.key] = '';
      }
    });
    
    if (value) {
      const availableProducts = this.getAvailableProducts();
      
      this.log(`${availableProducts.length} products available after selection`);
      
      if (index + 1 < this.attributes.length) {
        const nextAttr = this.attributes[index + 1];
        const nextSelect = this.selectors[nextAttr.key];
        
        this.populateDropdown(nextAttr.key, availableProducts);
        nextSelect.disabled = false;
      }
      
      this.updatePriceDisplay();
      this.checkAllSelected();
      
    } else {
      this.updatePriceDisplay();
      this.clearProductInfo();
    }
  }
  
  getAvailableProducts() {
    return this.childProducts.filter(product => {
      for (const [key, value] of Object.entries(this.selections)) {
        if (value && product.metafields[key] !== value) {
          return false;
        }
      }
      return true;
    });
  }
  
  checkAllSelected() {
    const allSelected = this.attributes.every(attr => {
      return this.selections[attr.key] && this.selections[attr.key] !== '';
    });
    
    if (allSelected) {
      this.selectedProduct = this.childProducts.find(product => {
        return this.attributes.every(attr => {
          return product.metafields[attr.key] === this.selections[attr.key];
        });
      });
      
      if (this.selectedProduct) {
        this.log('Product selected:', this.selectedProduct);
        this.showProductInfo();
      } else {
        this.log('No product matches selections', 'warn');
        this.showUnavailable();
      }
    } else {
      this.clearProductInfo();
    }
  }
  
  updatePriceDisplay() {
    const availableProducts = this.getAvailableProducts();
    
    if (availableProducts.length === 0) {
      this.priceStartEl.textContent = 'No products available';
      return;
    }
    
    let minPrice = Infinity;
    availableProducts.forEach(product => {
      if (product.available && product.price < minPrice) {
        minPrice = product.price;
      }
    });
    
    if (minPrice === Infinity) {
      const anyProducts = availableProducts.length > 0;
      this.priceStartEl.textContent = anyProducts ? 'Out of stock' : 'No products available';
    } else {
      this.priceStartEl.textContent = this.formatMoney(minPrice) + ' +VAT';
    }   
  }
  
  showProductInfo() {
    if (!this.selectedProduct) return;
    
    const priceStartEl = this.container.querySelector('#config-price-start');
    if (priceStartEl) {
      priceStartEl.style.display = 'none';
    }
    
    this.configInfoEl.style.display = 'block';
    
    const price = this.formatMoney(this.selectedProduct.price);
    let priceHTML = `<span class="price">${price} +VAT</span>`;
    
    if (this.selectedProduct.compare_at_price && this.selectedProduct.compare_at_price > this.selectedProduct.price) {
      const comparePrice = this.formatMoney(this.selectedProduct.compare_at_price);
      priceHTML += ` <span class="compare-price">${comparePrice}</span>`;
    }
    
    this.priceEl.innerHTML = priceHTML;
    
    const available = this.selectedProduct.available;
    this.availabilityEl.innerHTML = available
      ? '<span class="in-stock">In Stock</span>'
      : '<span class="out-of-stock">Out of Stock</span>';
    this.availabilityEl.className = `config-availability ${available ? 'available' : 'unavailable'}`;
    
    if (this.selectedProduct.sku) {
      this.skuEl.innerHTML = `<strong>SKU:</strong> ${this.selectedProduct.sku}`;
    }
    
    this.updateBulkDiscount();
    
    this.addToCartBtn.disabled = !available;
    this.enableQuantitySelector();
    
    this.log('Product info displayed');
  }
  
  clearProductInfo() {
    const priceStartEl = this.container.querySelector('#config-price-start');
    if (priceStartEl) {
      priceStartEl.style.display = 'block';
    }
    
    this.configInfoEl.style.display = 'none';
    
    this.priceEl.textContent = '';
    this.availabilityEl.textContent = '';
    this.skuEl.textContent = '';
    
    if (this.bulkDiscountEl) {
      this.bulkDiscountEl.innerHTML = '';
    }
    
    this.addToCartBtn.disabled = true;
    this.disableQuantitySelector();
    
    this.selectedProduct = null;
  }
  
  updateBulkDiscount() {
    if (!this.selectedProduct || !this.bulkDiscountEl) return;
    
    const basePrice = this.selectedProduct.price;
    if (!basePrice || basePrice <= 0) {
      this.bulkDiscountEl.innerHTML = '';
      return;
    }
    
    // Check which tiered discount tag the product has
    let hasVeluxDiscount = false;
    let hasDakeaDiscount = false;
    
    if (this.selectedProduct.tags) {
      if (Array.isArray(this.selectedProduct.tags)) {
        // Tags are already an array
        hasVeluxDiscount = this.selectedProduct.tags.some(tag => 
          tag.trim().toLowerCase() === 'velux tiered discount'
        );
        hasDakeaDiscount = this.selectedProduct.tags.some(tag => 
          tag.trim().toLowerCase() === 'dakea tiered discount'
        );
      } else if (typeof this.selectedProduct.tags === 'string') {
        // Tags are a comma-separated string
        const tagsArray = this.selectedProduct.tags.split(',').map(t => t.trim().toLowerCase());
        hasVeluxDiscount = tagsArray.includes('velux tiered discount');
        hasDakeaDiscount = tagsArray.includes('dakea tiered discount');
      }
    }
    
    // If no discount tag, hide the element
    if (!hasVeluxDiscount && !hasDakeaDiscount) {
      this.bulkDiscountEl.innerHTML = '';
      return;
    }
    
    let html = '';
    
    // VELUX Tiered Discount (2 tiers)
    if (hasVeluxDiscount) {
      const price3 = basePrice * 0.98;
      const price5 = basePrice * 0.95;
      
      html = `
        <div style="padding: 12px; background: #f9f9f9; border-radius: 5px; font-size: 14px; line-height: 1.6; margin-top: 12px;">
          <div style="margin-bottom: 4px;">
            <strong>Buy 3</strong> for <span style="color: #e4332a; font-weight: 600;">${this.formatMoney(price3)}</span> each and save <strong>2%</strong>
          </div>
          <div>
            <strong>Buy 5+</strong> for <span style="color: #e4332a; font-weight: 600;">${this.formatMoney(price5)}</span> each and save <strong>5%</strong>
          </div>
        </div>
      `;
    }
    
    // Dakea Tiered Discount (3 tiers)
    if (hasDakeaDiscount) {
      const price3 = basePrice * 0.98;
      const price4 = basePrice * 0.96;
      const price5 = basePrice * 0.95;
      
      html = `
        <div style="padding: 12px; background: #f9f9f9; border-radius: 5px; font-size: 14px; line-height: 1.6; margin-top: 12px;">
          <div style="margin-bottom: 4px;">
            <strong>Buy 3</strong> for <span style="color: #e4332a; font-weight: 600;">${this.formatMoney(price3)}</span> each and save <strong>2%</strong>
          </div>
          <div style="margin-bottom: 4px;">
            <strong>Buy 4</strong> for <span style="color: #e4332a; font-weight: 600;">${this.formatMoney(price4)}</span> each and save <strong>4%</strong>
          </div>
          <div>
            <strong>Buy 5+</strong> for <span style="color: #e4332a; font-weight: 600;">${this.formatMoney(price5)}</span> each and save <strong>5%</strong>
          </div>
        </div>
      `;
    }
    
    this.bulkDiscountEl.innerHTML = html;
  }
  
  showUnavailable() {
    this.configInfoEl.style.display = 'block';
    
    const priceStartEl = this.container.querySelector('#config-price-start');
    if (priceStartEl) {
      priceStartEl.style.display = 'none';
    }
    
    this.priceEl.textContent = '';
    this.availabilityEl.textContent = 'This combination is not available';
    this.availabilityEl.className = 'config-availability unavailable';
    this.skuEl.textContent = '';
    this.addToCartBtn.disabled = true;
    
    this.log('Showing unavailable message');
  }
  
  async addToCart() {
    const variantId = this.selectedProduct.variant_id || this.selectedProduct.variantId;
    
    if (!this.selectedProduct || !variantId) {
      this.log('Cannot add to cart: No product selected', 'warn');
      return;
    }
    
    this.log(`Adding to cart: ${this.selectedProduct.sku}`);
    
    const quantity = parseInt(this.quantityInput?.value) || 1;
    
    // Disable button and show loading state
    this.addToCartBtn.disabled = true;
    const addToCartText = this.addToCartBtn.querySelector('.add-to-cart-text__content');
    const originalText = addToCartText.textContent;
    addToCartText.textContent = 'Adding...';
    
    try {
      const formData = new FormData();
      formData.append('id', variantId);
      formData.append('quantity', quantity);
      
      const response = await fetch('/cart/add', {
        method: 'POST',
        body: formData
      });
      
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }
      
      const responseText = await response.text();
      this.log('Added to cart successfully');
      
      // Reset text and add animation class
      addToCartText.textContent = originalText;
      this.addToCartBtn.classList.add('atc-added');
      
      document.dispatchEvent(new CustomEvent('product:added-to-cart', {
        bubbles: true,
        detail: { variantId: variantId, quantity: quantity }
      }));
      
      await this.triggerCartUpdate();
      
      setTimeout(() => {
        this.addToCartBtn.classList.remove('atc-added');
        this.addToCartBtn.disabled = false;
      }, 2000);
      
    } catch (error) {
      this.log('Failed to add to cart', 'error', error);
      
      // Error feedback
      addToCartText.textContent = 'Error - Try again';
      
      setTimeout(() => {
        addToCartText.textContent = originalText;
        this.addToCartBtn.disabled = false;
      }, 3000);
    }
  }
  
  async triggerCartUpdate() {
    this.log('Triggering cart update...');
    
    try {
      const cartDrawer = document.querySelector('cart-drawer-component');
      const cartItems = document.querySelector('cart-items-component');
      
      if (!cartItems) {
        this.log('Cart items component not found');
        if (cartDrawer && cartDrawer.open) {
          cartDrawer.open();
        }
        return;
      }
      
      const sectionId = cartItems.dataset.sectionId || cartItems.getAttribute('data-section-id');
      
      if (sectionId) {
        this.log('Refreshing cart section:', sectionId);
        
        const response = await fetch(`${window.location.pathname}?section_id=${sectionId}`);
        const html = await response.text();
        
        const parser = new DOMParser();
        const doc = parser.parseFromString(html, 'text/html');
        const newCartItems = doc.querySelector('cart-items-component');
        
        if (newCartItems) {
          cartItems.innerHTML = newCartItems.innerHTML;
          this.log('Cart items refreshed');
        }
      }
      
      document.dispatchEvent(new CustomEvent('cart:updated', { bubbles: true }));
      document.body.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
      
      fetch('/cart.js')
        .then(res => res.json())
        .then(cart => {
          this.log('Updating cart count to:', cart.item_count);
          
          const countElements = document.querySelectorAll('.cart-bubble__text-count, [data-cart-count], .cart-count');
          countElements.forEach(el => {
            el.textContent = cart.item_count;
            
            if (cart.item_count > 0) {
              el.classList.remove('hidden');
              
              const bubble = el.closest('.cart-bubble');
              if (bubble) {
                bubble.classList.remove('hidden');
              }
            }
          });
        })
        .catch(err => this.log('Failed to update cart count', 'error', err));
      
      if (cartDrawer && cartDrawer.open) {
        this.log('Opening cart drawer');
        setTimeout(() => {
          cartDrawer.open();
        }, 100);
      }
      
    } catch (error) {
      this.log('Failed to trigger cart update', 'error', error);
      
      const cartDrawer = document.querySelector('cart-drawer-component');
      if (cartDrawer && cartDrawer.open) {
        cartDrawer.open();
      }
    }
  }
  
  formatMoney(cents) {
    const amount = (cents / 100).toFixed(2);
    
    if (window.Shopify && window.Shopify.formatMoney) {
      return window.Shopify.formatMoney(cents);
    }
    
    return `£${amount}`;
  }
  
  showLoading() {
    const loadingHTML = `
      <div class="config-loading">
        <div class="config-loading-spinner"></div>
        <p>Loading options...</p>
      </div>
    `;
    this.container.insertAdjacentHTML('beforeend', loadingHTML);
  }
  
  hideLoading() {
    const loading = this.container.querySelector('.config-loading');
    if (loading) {
      loading.remove();
    }
  }
  
  showError(message) {
    this.hideLoading();
    
    const errorHTML = `
      <div class="config-error">
        <p>${message}</p>
      </div>
    `;
    this.container.insertAdjacentHTML('beforeend', errorHTML);
  }
  
  log(message, level = 'info', data = null) {
    if (!this.config.enableDebugMode) return;
    
    const prefix = '[ConfigurableProduct]';
    
    switch (level) {
      case 'error':
        console.error(prefix, message, data);
        break;
      case 'warn':
        console.warn(prefix, message, data);
        break;
      default:
        console.log(prefix, message, data);
    }
  }
}

function initializeConfigurableProducts() {
  const containers = document.querySelectorAll('[data-child-products]');
  
  if (containers.length === 0) {
    console.log('[ConfigurableProduct] No configurable products found on page');
    return;
  }
  
  console.log(`[ConfigurableProduct] Found ${containers.length} configurable product(s)`);
  
  containers.forEach(container => {
    try {
      new ConfigurableProduct(container);
    } catch (error) {
      console.error('[ConfigurableProduct] Failed to initialize:', error);
    }
  });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initializeConfigurableProducts);
} else {
  initializeConfigurableProducts();
}